import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  DollarSign,
  LineChart,
  Layers,
  RefreshCw,
  Loader2,
  ChevronRight,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { LRDirectAtlasCard, LRDirectAtlasCardHeader } from "./LRDirectAtlasCard";
import { useLRDirectRepricing, type RepricingPost } from "@/hooks/useLRDirectRepricing";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * LR Direct ATLAS Load Repricing — real data only, no demo/placeholder numbers.
 * Scans the shipper's open direct_loads and flags target rates >8% under market.
 * Read-only: never writes to direct_loads.
 */
export default function LRDirectRepricingCard() {
  const navigate = useNavigate();
  const { scan, loading, result, progress, error, hasScanned } = useLRDirectRepricing();
  const [dialogOpen, setDialogOpen] = useState(false);

  const offMarket = result?.offMarket ?? [];
  const top = offMarket.slice(0, 3);

  const stats = [
    { label: "Scanned", value: hasScanned ? String(result!.scanned) : "—", icon: Layers },
    { label: "Off-market", value: hasScanned ? String(offMarket.length) : "—", icon: LineChart },
    {
      label: "Uplift",
      value: hasScanned && result!.totalUplift > 0 ? `+$${Math.round(result!.totalUplift).toLocaleString()}` : "—",
      icon: RefreshCw,
    },
  ];

  return (
    <>
      <LRDirectAtlasCard className="flex flex-col">
        <LRDirectAtlasCardHeader
          icon={<TrendingUp className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
          title="ATLAS Load Repricing"
        >
          <div className="flex items-center gap-1.5">
            {hasScanned && !loading && (
              <button
                onClick={() => scan()}
                className="transition-opacity hover:opacity-70"
                style={{ color: "hsl(170 50% 15%)" }}
                aria-label="Rescan"
              >
                <RefreshCw className="h-3 w-3" />
              </button>
            )}
            <span
              className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ backgroundColor: "hsl(170 50% 15% / 0.12)", color: "hsl(170 50% 15%)" }}
            >
              Live
            </span>
          </div>
        </LRDirectAtlasCardHeader>


        <div className="px-3 pb-3 pt-3 flex flex-col flex-1 space-y-2.5">
          {/* Banner */}
          <div
            className="rounded-md px-3 py-2 flex items-center gap-2"
            style={{
              backgroundColor: offMarket.length > 0 ? "hsl(35 95% 50% / 0.10)" : "hsl(var(--brand-teal-deep) / 0.10)",
            }}
          >
            <DollarSign
              className="h-3 w-3 shrink-0"
              style={{ color: offMarket.length > 0 ? "hsl(35 95% 50%)" : "hsl(var(--brand-teal-deep))" }}
            />
            <span
              className="text-[11px] font-semibold"
              style={{ color: offMarket.length > 0 ? "hsl(35 80% 42%)" : "hsl(var(--brand-teal-deep))" }}
            >
              {!hasScanned
                ? "Spot direct loads priced off-market"
                : offMarket.length > 0
                ? `${offMarket.length} open post${offMarket.length !== 1 ? "s" : ""} targeting below market`
                : "No open posts are targeting below market"}
            </span>
          </div>

          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Scan your open LR Direct posts and see which target rates are drifting from the market so you can
            adjust before the bid deadline.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-1.5">
            {stats.map((s) => (
              <div key={s.label} className="rounded-md px-2 py-1.5 bg-muted/50">
                <div className="flex items-center gap-1 mb-0.5">
                  <s.icon className="h-2.5 w-2.5 text-muted-foreground" />
                  <span className="text-[9px] text-muted-foreground uppercase tracking-wide truncate">
                    {s.label}
                  </span>
                </div>
                <span
                  className="text-xs font-semibold"
                  style={{ color: hasScanned ? "hsl(var(--brand-teal-deep))" : "hsl(var(--muted-foreground) / 0.7)" }}
                >
                  {s.value}
                </span>
              </div>
            ))}
          </div>

          {/* Body */}
          <div className="space-y-1">
            <span className="text-[9px] text-muted-foreground uppercase tracking-wide font-medium">
              Top affected lanes
            </span>

            {loading ? (
              <div className="flex items-center gap-2 py-3 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span className="text-[11px]">
                  {progress.total > 0
                    ? `Analyzing ${progress.done} of ${progress.total} posts…`
                    : "Loading your open posts…"}
                </span>
              </div>
            ) : error ? (
              <div className="flex items-start gap-1.5 py-2">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0 text-destructive" />
                <span className="text-[11px] text-destructive">{error}</span>
              </div>
            ) : !hasScanned ? (
              <p className="text-[11px] text-muted-foreground py-2">
                Scan your open posts to see real market comparisons. No results yet.
              </p>
            ) : top.length === 0 ? (
              <p className="text-[11px] text-muted-foreground py-2">
                {result!.scanned === 0
                  ? "You have no open LR Direct posts to analyze."
                  : result!.noTarget === result!.scanned
                  ? `None of your ${result!.scanned} open post${result!.scanned !== 1 ? "s" : ""} has a target rate set — brokers are bidding blind.`
                  : "All open posts with a target rate are at or above market."}
              </p>
            ) : (
              top.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground font-medium truncate mr-2">
                    {p.originCity}, {p.originState} → {p.destCity}, {p.destState}
                  </span>
                  <span className="font-semibold shrink-0" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                    +${Math.round(p.delta).toLocaleString()}
                  </span>
                </div>
              ))
            )}

            {hasScanned && !loading && result!.noTarget > 0 && top.length > 0 && (
              <p className="text-[10px] text-muted-foreground pt-1">
                {result!.noTarget} open post{result!.noTarget !== 1 ? "s" : ""} skipped — no target rate set.
              </p>
            )}
          </div>

          {/* CTA */}
          <div className="mt-auto pt-1">
            {offMarket.length > 0 && !loading ? (
              <button
                onClick={() => setDialogOpen(true)}
                className="flex items-center gap-1 text-[11px] font-semibold w-full rounded-md px-3 py-2 transition-colors"
                style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
              >
                Review {offMarket.length} Suggestion{offMarket.length !== 1 ? "s" : ""}
                <ChevronRight className="h-3 w-3 ml-auto" />
              </button>
            ) : (
              <button
                onClick={() => scan()}
                disabled={loading}
                className="flex items-center justify-center gap-2 text-[11px] font-semibold w-full rounded-md px-3 py-3 transition-colors disabled:opacity-60"
                style={{ backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))" }}
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5" />}
                {hasScanned ? "Rescan Open Posts" : "Scan Your Open Posts"}
              </button>
            )}
          </div>
        </div>
      </LRDirectAtlasCard>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Target rate suggestions</DialogTitle>
            <DialogDescription className="text-xs">
              These open posts target more than 8% under the market rate for their lane, so they may not attract
              bids before the deadline. Open a post to raise its target range.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {offMarket.map((p) => (
              <SuggestionRow
                key={p.id}
                post={p}
                onOpen={() =>
                  navigate(
                    p.postType === "quote"
                      ? `/lr-direct/my-quotes`
                      : `/lr-direct/live/${p.id}`,
                  )
                }
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SuggestionRow({ post: p, onOpen }: { post: RepricingPost; onOpen: () => void }) {
  const urgent = p.hoursToDeadline != null && p.hoursToDeadline > 0 && p.hoursToDeadline <= 6;
  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {p.originCity}, {p.originState} → {p.destCity}, {p.destState}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {p.loadNumber} · {Math.round(p.totalMiles).toLocaleString()} mi
            {p.postType ? ` · ${p.postType === "quote" ? "Quote request" : "Load"}` : ""}
          </p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[11px] shrink-0" onClick={onOpen}>
          Open post
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md bg-muted/50 px-2 py-1.5">
          <span className="block text-[9px] uppercase tracking-wide text-muted-foreground">Your target</span>
          <span className="text-xs font-semibold text-foreground">
            ${p.currentTarget.toLocaleString()}
          </span>
          <span className="block text-[10px] text-muted-foreground">${p.currentRpm.toFixed(2)}/mi</span>
        </div>
        <div className="rounded-md bg-muted/50 px-2 py-1.5">
          <span className="block text-[9px] uppercase tracking-wide text-muted-foreground">Market</span>
          <span className="text-xs font-semibold text-foreground">${p.marketRpm.toFixed(2)}/mi</span>
          <span className="block text-[10px] text-muted-foreground">
            {p.tierLabel}
            {p.compCount > 0 ? ` · ${p.compCount} comps` : ""}
          </span>
        </div>
        <div className="rounded-md px-2 py-1.5" style={{ backgroundColor: "hsl(170 85% 32% / 0.10)" }}>
          <span className="block text-[9px] uppercase tracking-wide text-muted-foreground">Suggested</span>
          <span className="text-xs font-semibold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
            ${p.suggestedTarget.toLocaleString()}
          </span>
          <span className="block text-[10px] text-muted-foreground">
            +${p.delta.toLocaleString()} · ${p.suggestedRpm.toFixed(2)}/mi
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-[10px]">
        <Clock className={`h-3 w-3 ${urgent ? "text-destructive" : "text-muted-foreground"}`} />
        <span className={urgent ? "text-destructive font-semibold" : "text-muted-foreground"}>
          {p.hoursToDeadline == null
            ? "No bid deadline set"
            : p.hoursToDeadline <= 0
            ? "Bid deadline passed"
            : `Bids close in ${p.hoursToDeadline < 1 ? "<1" : Math.round(p.hoursToDeadline)}h`}
        </span>
        <span className="text-muted-foreground/70">· confidence: {p.confidence}</span>
      </div>
    </div>
  );
}
